// ============================================================
// InspectionDetails — Ollie Inspection Readiness Report page
//
// This page subscribes (real-time WebSocket via Hasura) to a
// single inspection_job and renders a full Readiness Report:
//   • Header with property / org / jurisdiction / type tags
//   • Status banners when Ollie is ANALYZING or FAILED
//   • Quick-stat cards (Ollie status, score, est. time on-site)
//   • Per-category accordion (findings + deficiencies per category)
//   • Sidebar: on-site verification checklist + summary counters
//   • EvidenceModal: full-screen image/video viewer with feedback
// ============================================================
import { useCallback, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
	Building2,
	MapPin,
	Landmark,
	FileText,
	Download,
	ArrowLeft,
	CheckCircle2,
	AlertTriangle,
	XCircle,
	CircleDashed,
	Zap,
	Clock,
	Eye,
	ChevronDown,
	ClipboardList,
	Sparkles,
	Loader2,
	HelpCircle
} from "lucide-react";

import OllieLayout from "components/layouts/ollie-layout/ollie-layout";
import EvidenceModal from "components/evidence-modal/evidence-modal";
import Icon, { ICON_SOURCES, IconSource } from "components/icons/icon";
import OllieAvatar, { ollieMoodForReadiness } from "components/ollie-avatar/ollie-avatar";

import usePagePdfExport from "hooks/usePagePdfExport";

import { useLoggedUser } from "state/reactive-vars/logged-user-var";

import { buildReportFileName } from "utils/page-pdf";
import { getBuildingTypeLabel, getInspectionTypeLabel, getPropertyAddress } from "utils/helpers";
import routeNames from "constants/route-names";
import { useDescribeScreenToOllie } from "state/reactive-vars/ollie-screen-description-var";
import { buildInspectionJobDescription } from "utils/ollie-screen-description";
import {
	CONSTRUCTION_TYPE_OPTIONS,
	FIRE_SUPPRESSION_OPTIONS,
	OCCUPANCY_CLASS_OPTIONS
} from "constants/property-field-options";
import {
	InspectionJobStatus,
	InspectionGapAnalysisStatus,
	InspectionGapAnalysisSeverity,
	UserRoleValue
} from "constants/field-values";

import {
	useGetInspectionDetailsByIdSubscription,
	GetInspectionDetailsByIdSubscription
} from "graphql/subscriptions/get-inspection-details-by-id.generated";

import {
	type ReadinessStatus,
	type GapAnalysisItem,
	isBlockingGap,
	formatEstimatedTime,
	computeEstimatedOnsiteMinutes,
	deriveReadiness
} from "routes/inspector-dashboard/inspector-dashboard";

// ── Type aliases ─────────────────────────────────────────────────────────────
// Derive row shapes directly from the generated subscription type so they stay
// in sync automatically whenever the GraphQL schema changes.

/** The full inspection job row (non-nullable, subscription root). */
type InspectionJob = NonNullable<GetInspectionDetailsByIdSubscription["inspection_jobs_by_pk"]>;
/** A single row from inspection_checklist_items nested inside the job. */
type ChecklistItem = InspectionJob["inspection_checklist_items"][number];

// ── UI-level status union types ───────────────────────────────────────────────
/** Per-category badge status:
 *  ready       → all gaps PASS, all findings PRESENT
 *  warning     → at least one non-blocking gap or non-PRESENT finding
 *  fail        → at least one CRITICAL/HIGH FAIL gap
 *  no-evidence → every item is missing (no gaps, findings, or submissions) */
type CategoryStatus = "ready" | "warning" | "fail" | "no-evidence";

// ── View-model interfaces ─────────────────────────────────────────────────────
// These interfaces are flat, UI-friendly shapes derived from the raw GQL rows.
// They are built inside the `categories` useMemo and consumed directly in JSX.

/** Visual treatment for a gap row, derived from its severity. */
type GapVisual = "ready" | "warning" | "fail" | "skipped";

/** Fully resolved view-model for one inspection category (e.g. "Fire Alarm System").
 *  Built once per render from inspection_checklist_items grouped by category. */
interface CategoryView {
	key: string;
	label: string;
	iconSource: IconSource;
	iconName: string;
	itemsChecked: number;
	totalItems: number;
	status: CategoryStatus;
	items: ChecklistItem[];
}

// ── Static config maps ───────────────────────────────────────────────────────
// Maps a status/readiness key to its display label, CSS classes, and icon.
// Adding a new status requires only a new entry here — JSX picks it up automatically.

/** Maps CategoryStatus → { label, badgeClass, icon } for per-category accordion headers. */
const categoryStatusConfig = {
	ready: { label: "Verified", badgeClass: "rr-badge-ready", icon: CheckCircle2 },
	warning: { label: "Review Needed", badgeClass: "rr-badge-warning", icon: AlertTriangle },
	fail: { label: "Issue Found", badgeClass: "rr-badge-fail", icon: XCircle },
	"no-evidence": { label: "No Evidence", badgeClass: "rr-badge-no-evidence", icon: CircleDashed }
} as const;

/** Maps GapVisual → { Icon, iconClass, textClass } for the per-item rows
 *  inside a category accordion. Visual is derived from gap severity. */
const gapVisualConfig = {
	ready: { Icon: CheckCircle2, iconClass: "rr-finding-check", badgeClass: "rr-badge-ready", textClass: "" },
	warning: {
		Icon: AlertTriangle,
		iconClass: "rr-finding-warning-icon",
		badgeClass: "rr-badge-warning",
		textClass: "rr-finding-text-warning"
	},
	fail: {
		Icon: AlertTriangle,
		iconClass: "rr-finding-fail-icon",
		badgeClass: "rr-badge-fail",
		textClass: "rr-finding-text-fail"
	},
	skipped: {
		Icon: CircleDashed,
		iconClass: "rr-finding-skipped-icon",
		badgeClass: "rr-badge-skipped",
		textClass: "rr-finding-text-skipped"
	}
} as const;

/** Maps ReadinessStatus → { label, cardClass, textClass, icon }
 *  for the top-level Ollie Status stat card. Must stay in sync with the
 *  identical statusConfig in inspector-dashboard.tsx. */
const ollieStatusConfig = {
	"fast-lane": {
		label: "Fast Lane",
		cardClass: "rr-ollie-card-fast-lane",
		textClass: "rr-ollie-text-fast-lane",
		icon: Zap
	},
	review: {
		label: "Review",
		cardClass: "rr-ollie-card-review",
		textClass: "rr-ollie-text-review",
		icon: AlertTriangle
	},
	"not-ready": {
		label: "Not Ready",
		cardClass: "rr-ollie-card-not-ready",
		textClass: "rr-ollie-text-not-ready",
		icon: XCircle
	},
	"in-progress": {
		label: "In progress",
		cardClass: "rr-ollie-card-review",
		textClass: "rr-ollie-text-review",
		icon: HelpCircle
	}
} as const;

// ── Pure helper functions ────────────────────────────────────────────────────

/** True when an item has no gaps, no findings, and no submissions — it was never
 *  inspected and renders greyed-out ("missing") in the UI. */
const isMissingItem = (item: ChecklistItem): boolean =>
	item.gap_analysis_items.length === 0 && item.evidence_findings.length === 0 && item.evidence_submissions.length === 0;

/** Returns true for gaps that need attention but are not hard blockers:
 *  - status WARNING or UNINSPECTED
 *  - status FAIL but below CRITICAL/HIGH severity */
const isWarningGap = (gap: GapAnalysisItem): boolean =>
	gap.status === InspectionGapAnalysisStatus.WARNING ||
	gap.status === InspectionGapAnalysisStatus.UNINSPECTED ||
	(gap.status === InspectionGapAnalysisStatus.FAIL && !isBlockingGap(gap));

/** Derives the CategoryStatus badge for one category group.
 *  Priority: all items missing → "no-evidence" > blocking gap → "fail"
 *  > warning gap → "warning" > "ready". */
const deriveCategoryStatus = (items: ChecklistItem[], gaps: GapAnalysisItem[]): CategoryStatus => {
	if (items.length > 0 && items.every(isMissingItem)) return "no-evidence";
	if (gaps.some(isBlockingGap)) return "fail";
	if (gaps.some(isWarningGap)) return "warning";
	return "ready";
};

/** Maps gap severity → row visual treatment. CRITICAL/HIGH render as red fail,
 *  MEDIUM as amber warning, INFO (or unknown) as green ready. */
const severityToVisual = (severity: string | null | undefined): GapVisual => {
	if (severity === InspectionGapAnalysisSeverity.CRITICAL || severity === InspectionGapAnalysisSeverity.HIGH)
		return "fail";
	if (severity === InspectionGapAnalysisSeverity.MEDIUM) return "warning";
	return "ready";
};

/** Display label for a property field stored as a picker code (e.g. "R-2", "V-B", "wet_pipe").
 *  The picker label spells the code out and then gives examples between parentheses; the
 *  parenthetical is dropped here because these render as one line inside a narrow column. */
const propertyFieldLabel = (
	options: ReadonlyArray<{ value: string; label: string }>,
	value: string | null | undefined
): string | null => {
	if (!value) return null;
	const label = options.find(option => option.value === value)?.label ?? value;
	return label.replace(/\s*\([^)]*\)\s*$/, "");
};

/** InspectionDetails — main page component.
 *  Reads :jobId from the URL, subscribes to the inspection_job in real-time,
 *  and renders the full readiness report. */
const InspectionDetails = () => {
	/** jobId comes from the URL param defined in the router (e.g. /ollie/inspection/:jobId). */
	const { jobId } = useParams<{ jobId: string }>();

	const { loggedUserValue } = useLoggedUser();
	/** Internal job diagnostics (confidence, pipeline state, org type) are only for platform admins. */
	const isPlatformAdmin = loggedUserValue.role === UserRoleValue.admin_user;

	/** Real-time WebSocket subscription to a single inspection_job by PK.
	 *  Hasura pushes updates automatically whenever any related row changes
	 *  (state, gap_analysis_items, inspector_feedbacks, etc.).
	 *  No manual polling is needed. Skipped until jobId is available. */
	const { data, loading } = useGetInspectionDetailsByIdSubscription({
		skip: !jobId,
		variables: { id: jobId! }
	});

	/** The inspection_job row; undefined while loading or if the ID is invalid. */
	const job = data?.inspection_jobs_by_pk;
	/** Current pipeline state of the job (COLLECTING, ANALYZING, COMPLETE, FAILED…). */
	const jobState = job?.state as InspectionJobStatus | undefined;
	// Tells Ollie which report is on screen, so a question about "this" has a subject.
	useDescribeScreenToOllie(
		buildInspectionJobDescription({
			jobId: job?.id,
			propertyName: job?.property?.name,
			jurisdictionName: job?.jurisdiction_record?.name || job?.jurisdiction,
			inspectionType: job?.inspection_type,
			state: job?.state,
			irisScore: job?.iris_score,
			checklistItemCount: job?.inspection_checklist_items?.length,
			propertyId: job?.property_id,
			jurisdictionId: job?.jurisdiction_id,
			organizationId: job?.organization_id,
			workspaceId: job?.workspace_id,
			createdByUserId: job?.created_by_user_id
		}),
		{ propertyId: job?.property_id, jurisdictionId: job?.jurisdiction_id }
	);


	/** Id of the checklist item the inspector clicked; drives the <EvidenceModal> visibility.
	 *  We store only the id (not the row) and re-read the live item from `job` below, so the
	 *  modal always reflects the latest subscription data. null → modal closed. */
	const [selectedItemId, setSelectedItemId] = useState<string | null>(null);

	/** Tracks which category accordions are expanded (key = category string, value = expanded?).
	 *  Default state (key absent) is treated as true (expanded) by toggleCategory. */
	const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
	const { exportRef, isExporting, exportToPdf } = usePagePdfExport<HTMLDivElement>();

	/** Picks the most recently created gap_analysis_item from an array. */
	const latestGap = useCallback(
		(gaps: GapAnalysisItem[]): GapAnalysisItem =>
			[...gaps].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0],
		[]
	);

	const latestFinding = useCallback(
		(findings: ChecklistItem["evidence_findings"]): ChecklistItem["evidence_findings"][0] =>
			[...findings].sort((a, b) => new Date(b.created_at ?? "").getTime() - new Date(a.created_at ?? "").getTime())[0],
		[]
	);

	const getItemAssessment = useCallback(
		(item: ChecklistItem): { visual: GapVisual; reason: string | null; isSkipped: boolean } => {
			const isSkipped = isMissingItem(item);

			if (item.gap_analysis_items.length > 0) {
				const gap = latestGap(item.gap_analysis_items);
				return {
					visual: gap.status === "PASS" ? "ready" : severityToVisual(gap.severity),
					reason: gap.reason,
					isSkipped
				};
			}
			if (item.evidence_findings.length > 0) {
				const finding = latestFinding(item.evidence_findings);
				let visual: GapVisual = "ready";
				if (finding.provisional_status === "MISSING" || finding.provisional_status === "DAMAGED") visual = "fail";
				else if (finding.provisional_status === "UNCERTAIN" || finding.provisional_status === "OBSTRUCTED")
					visual = "warning";
				return { visual, reason: finding.provisional_reason ?? null, isSkipped };
			}

			return { visual: isSkipped ? "skipped" : "ready", reason: null, isSkipped };
		},
		[latestGap, latestFinding]
	);

	/** Splits a category's items the way the accordion renders them: the ones that read as
	 *  fine first, then the deficiencies under their own heading. Both the accordion and the
	 *  modal's Previous/Next order read from here, so the two can't drift apart. */
	const splitCategoryItems = useCallback(
		(items: ChecklistItem[]): { normalItems: ChecklistItem[]; deficiencyItems: ChecklistItem[] } => {
			const isDeficiency = (item: ChecklistItem) => {
				const visual = getItemAssessment(item).visual;
				return visual === "warning" || visual === "fail";
			};
			return {
				normalItems: items.filter(item => !isDeficiency(item)),
				deficiencyItems: items.filter(isDeficiency)
			};
		},
		[getItemAssessment]
	);

	/** The live checklist item matching the modal selection, re-read from `job` on every
	 *  subscription push so the modal always reflects the latest data (e.g. feedback just saved). */
	const selectedItem = useMemo(
		() => job?.inspection_checklist_items.find(checklistItem => checklistItem.id === selectedItemId) ?? null,
		[job, selectedItemId]
	);

	/** Derives the CategoryView[] array consumed by the accordion list.
	 *  For each category, every checklist item that has at least one
	 *  gap_analysis_item is rendered as a single row using the most recent gap. */
	const categories: CategoryView[] = useMemo(() => {
		if (!job) return [];

		/** Group all checklist items by their category key (e.g. "fire_alarm", "sprinkler"). */
		const itemsByCategory = new Map<string, ChecklistItem[]>();
		job.inspection_checklist_items.forEach(checklistItem => {
			const categoryKey = checklistItem.category ?? "uncategorized";
			if (!itemsByCategory.has(categoryKey)) itemsByCategory.set(categoryKey, []);
			itemsByCategory.get(categoryKey)!.push(checklistItem);
		});

		return Array.from(itemsByCategory.entries()).map(([categoryKey, categoryItems]) => {
			/** Pull display metadata (label, icon) from the first item that has it. */
			const categoryMeta = categoryItems.find(item => item.item_category)?.item_category;

			const gapsInCategory = categoryItems.flatMap(item =>
				item.gap_analysis_items.length > 0 ? [latestGap(item.gap_analysis_items)] : []
			);

			/** Count how many items already have at least one gap_analysis_items. */
			const itemsChecked = categoryItems.filter(item => item.gap_analysis_items.length > 0).length;
			const categoryStatus = deriveCategoryStatus(categoryItems, gapsInCategory);

			return {
				key: categoryKey,
				label: categoryMeta?.label ?? categoryKey,
				iconSource: (categoryMeta?.icon_source as IconSource) ?? ICON_SOURCES.LUCIDE,
				iconName: categoryMeta?.icon_name ?? "ShieldCheck",
				itemsChecked,
				totalItems: categoryItems.length,
				status: categoryStatus,
				items: categoryItems
			};
		});
	}, [job]);

	/** Every checklist item id in the order the report lays them out, so Previous/Next in the
	 *  evidence modal walks the list the reader is looking at. */
	const navigableItemIds: string[] = useMemo(
		() =>
			categories.flatMap(category => {
				const { normalItems, deficiencyItems } = splitCategoryItems(category.items);
				return [...normalItems, ...deficiencyItems].map(item => item.id);
			}),
		[categories, splitCategoryItems]
	);

	const selectedItemIndex = selectedItemId ? navigableItemIds.indexOf(selectedItemId) : -1;
	const previousItemId = selectedItemIndex > 0 ? navigableItemIds[selectedItemIndex - 1] : null;
	const nextItemId =
		selectedItemIndex >= 0 && selectedItemIndex < navigableItemIds.length - 1
			? navigableItemIds[selectedItemIndex + 1]
			: null;

	/** Flat array of the most recently created gap_analysis_item per checklist item.
	 *  Deduplicates across multiple analysis runs so stats are not double-counted. */
	const allGaps: GapAnalysisItem[] = useMemo(
		() =>
			(job?.inspection_checklist_items ?? []).flatMap(checklistItem =>
				checklistItem.gap_analysis_items.length > 0 ? [latestGap(checklistItem.gap_analysis_items)] : []
			),
		[job, latestGap]
	);

	/** Ordered list of corrective actions that require physical on-site verification.
	 *  Rules:
	 *    - gap must have requires_onsite_verification = true
	 *    - corrective_action must be a real string (not null / the literal "null" string
	 *      that the backend sometimes serialises for missing values)
	 *  Sorted ascending by priority_rank (lower rank = more urgent). */
	const onSiteChecklist: { id: string; text: string }[] = useMemo(
		() =>
			allGaps
				.filter(gap => gap.requires_onsite_verification && gap.corrective_action && gap.corrective_action !== "null")
				.sort((a, b) => (a.priority_rank ?? 999) - (b.priority_rank ?? 999))
				.map(gap => ({ id: gap.id, text: gap.corrective_action as string })),
		[allGaps]
	);

	/** Global counters shown in the Summary sidebar card.
	 *  totalItemsChecked: items with ≥1 evidence submission
	 *  verified:          gaps with status PASS
	 *  warnings:          gaps matching isWarningGap (non-blocking issues)
	 *  blocking:          gaps matching isBlockingGap (CRITICAL/HIGH FAIL)
	 *  missingEvidence:   items that are incomplete — no evidence submitted at all
	 *  citations:         total code citations across every gap */
	const totals = useMemo(() => {
		const allItems = job?.inspection_checklist_items ?? [];
		const totalItemsChecked = allItems.filter(checklistItem => checklistItem.gap_analysis_items.length > 0).length;
		const verified = allGaps.filter(gap => gap.status === InspectionGapAnalysisStatus.PASS).length;
		const warnings = allGaps.filter(isWarningGap).length;
		const blocking = allGaps.filter(isBlockingGap).length;
		const missingEvidence = allItems.filter(checklistItem => checklistItem.evidence_submissions.length === 0).length;
		const citations = allGaps.reduce((runningTotal, gap) => runningTotal + gap.citations.length, 0);
		return { totalItemsChecked, verified, warnings, blocking, missingEvidence, citations };
	}, [allGaps, job]);

	/** Top-level readiness label for the whole job — drives the Ollie Status stat card. */
	const readinessStatus: ReadinessStatus = useMemo(
		() => deriveReadiness(jobState, allGaps, job?.iris_score),
		[jobState, allGaps, job?.iris_score]
	);

	/** Visual config object (label, CSS classes, icon) for the current readiness status. */
	const ollieConfig = ollieStatusConfig[readinessStatus];
	/** Lucide icon component extracted for use in JSX. */
	const OllieIcon = ollieConfig.icon;
	/** Ollie score (0–100); shown in the stat card only when the job is COMPLETE. */
	const ollieScore = job?.iris_score ?? 0;
	/** Human-readable confidence label derived from iris_score thresholds. */
	const ollieConfidence = ollieScore >= 90 ? "High (90%+)" : ollieScore >= 75 ? "Medium" : "Low";

	// ── Convenience boolean flags derived from jobState ─────────────────────────
	/** True while Ollie is running gap analysis; drives the blue "analyzing" status banner. */
	const isAnalyzing = jobState === InspectionJobStatus.ANALYZING;
	/** True when gap analysis errored; drives the red "failed" status banner. */
	const isFailed = jobState === InspectionJobStatus.FAILED;
	/** True when analysis is done; unlocks the Export button and shows the ollie score. */
	const isComplete = jobState === InspectionJobStatus.COMPLETE;
	/** True while the inspector is still submitting evidence; shows the "Resume Collection" link. */
	const isCollecting = jobState === InspectionJobStatus.COLLECTING;

	/** True when the job is COMPLETE but some items were skipped (UNINSPECTED or no gap entry).
	 *  Allows the inspector to resume collection and re-run gap analysis. */
	const hasUninspectedItems = useMemo(() => {
		if (!isComplete) return false;
		const items = job?.inspection_checklist_items ?? [];
		return items.some(
			i =>
				i.gap_analysis_items.length === 0 ||
				latestGap(i.gap_analysis_items).status === InspectionGapAnalysisStatus.UNINSPECTED
		);
	}, [isComplete, job, latestGap]);

	/** The most-recent entry from inspection_job_state_logs, displayed in the Summary card.
	 *  Shows the last state transition (e.g. "ANALYZING → COMPLETE"). */
	const lastStateLog = useMemo(() => {
		const logs = job?.inspection_job_state_logs ?? [];
		if (!logs.length) return undefined;
		return [...logs].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
	}, [job]);

	/** The property's address as the rest of the app writes it: the standardized record when
	 *  there is one, and the free-text column only for properties captured before it existed. */
	const propertyAddress = getPropertyAddress(job?.property?.address, job?.property?.standardized_address);

	/** Building characteristics, shown on their own card under the header. Everything here is
	 *  optional on the property, and a property with no building hierarchy counts zero buildings
	 *  and floors, so only the values that were actually filled in get an entry. */
	const propertyDetails = useMemo(() => {
		const property = job?.property;
		if (!property) return [];

		const details = [
			{ label: "Occupancy Class", value: propertyFieldLabel(OCCUPANCY_CLASS_OPTIONS, property.occupancy_class) },
			{ label: "Number of Stories", value: property.num_stories },
			{
				label: "Building Type",
				value: propertyFieldLabel(CONSTRUCTION_TYPE_OPTIONS, property.construction_type)
			},
			{ label: "Year Built", value: property.year_built },
			{
				label: "Fire Suppression System",
				value: propertyFieldLabel(FIRE_SUPPRESSION_OPTIONS, property.sprinklered)
			},
			{ label: "Buildings", value: property.buildings_aggregate.aggregate?.count },
			{ label: "Floors", value: property.floors_aggregate.aggregate?.count },
			{ label: "Units", value: property.num_units }
		];

		// A field nobody filled in tells the inspector nothing, and a count of zero buildings
		// or floors only means the hierarchy was never captured. Both are dropped rather than
		// rendered as an empty row.
		return details.filter(detail => Boolean(detail.value));
	}, [job?.property]);

	// The PDF is a capture of the report as it renders here, so the only thing to build is
	// the file name: what tells two exports apart in a folder.
	const handleExport = useCallback(() => {
		if (!job) return;
		exportToPdf(buildReportFileName(job.property?.name || "Inspection", `Inspection ${job.id}`));
	}, [job, exportToPdf]);

	/** Opens the <EvidenceModal> for a checklist item. We pass only the id; the live row is
	 *  re-read from `job` (see `selectedItem`) and the modal derives everything else from it. */
	const openItemEvidence = useCallback((checklistItemId: string) => {
		setSelectedItemId(checklistItemId);
	}, []);

	/** Closes the modal. */
	const closeItemEvidence = useCallback(() => setSelectedItemId(null), []);

	/** Toggles the expand/collapse state of a category accordion.
	 *  Default (key absent in state) is treated as expanded (true). */
	const toggleCategory = useCallback((key: string) => {
		setExpandedCategories(prev => ({ ...prev, [key]: !(prev[key] ?? true) }));
	}, []);

	// ── Early return guards ─────────────────────────────────────────────────────
	/** Show a full-page spinner during the first data load (before any job row arrives). */
	if (loading && !job) {
		return (
			<OllieLayout>
				<div className="rr-not-found">
					<Loader2 className="rr-loading-spinner" />
					<p className="rr-not-found-text">Loading inspection report…</p>
				</div>
			</OllieLayout>
		);
	}

	/** If the subscription resolved but returned null, the job ID is invalid or access-denied. */
	if (!job) {
		return (
			<OllieLayout>
				<div className="rr-not-found">
					<p className="rr-not-found-text">Inspection job not found.</p>
					<Link to={routeNames.OLLIE_DASHBOARD_INSPECTIONS} className="rr-not-found-link">
						Return to Dashboard
					</Link>
				</div>
			</OllieLayout>
		);
	}

	return (
		<OllieLayout>
			<div className="rr-page">
				<div className="rr-inner" ref={exportRef}>
					{/* ── Top bar: back button + optional "Resume Collection" + Export ── */}
					<div className="rr-top-bar rr-animate" data-pdf-hide style={{ animationDelay: "0ms" }}>
						<Link to={routeNames.OLLIE_DASHBOARD_INSPECTIONS} className="rr-back-btn">
							<ArrowLeft className="rr-back-icon" />
							Dashboard
						</Link>
						<div className="rr-top-bar-actions">
							{(isCollecting || hasUninspectedItems) && (
								<Link to={routeNames.OLLIE_EVIDENCE_COLLECTION.replace(":jobId", job.id)} className="rr-resume-btn">
									<Sparkles className="rr-back-icon" />
									Resume Collection
								</Link>
							)}
							<button className="rr-export-btn" onClick={handleExport} disabled={!isComplete || isExporting}>
								<Download className="rr-export-icon" />
								{isExporting ? "Exporting…" : "Export"}
							</button>
						</div>
					</div>

					{/* ── Title area: eyebrow, property name, and tag row (address/org/type/jurisdiction) ── */}
					<div className="rr-title-area rr-animate" style={{ animationDelay: "80ms" }}>
						<p className="rr-eyebrow">Readiness Report</p>
						<h1 className="rr-property-title">{job.property?.name}</h1>
						<div className="rr-property-tags">
							{propertyAddress && (
								<span className="rr-property-tag">
									<MapPin className="rr-tag-icon" />
									{propertyAddress}
								</span>
							)}
							{job.property?.building_type && (
								<span className="rr-property-tag">
									<Building2 className="rr-tag-icon" />
									{getBuildingTypeLabel(job.property.building_type)}
								</span>
							)}
							<span className="rr-property-tag">
								<FileText className="rr-tag-icon" />
								{getInspectionTypeLabel(job.inspection_type)}
							</span>
							{(job.jurisdiction_record?.name || job.jurisdiction) && (
								<span className="rr-property-tag">
									<Landmark className="rr-tag-icon" />
									{job.jurisdiction_record?.name ?? job.jurisdiction}
								</span>
							)}
						</div>
					</div>

					{propertyDetails.length > 0 && (
						<div className="rr-property-details rr-animate" style={{ animationDelay: "120ms" }}>
							{propertyDetails.map(detail => (
								<div className="rr-property-detail" key={detail.label}>
									<span className="rr-property-detail-label">{detail.label}</span>
									<span className="rr-property-detail-value">{detail.value}</span>
								</div>
							))}
						</div>
					)}

					{/* ── Status banners (only one can show at a time) ──────────────────────── */}
					{/* ANALYZING: blue spinner banner while Ollie runs gap analysis. */}
					{isAnalyzing && (
						<div className="rr-status-banner rr-status-banner-analyzing rr-animate">
							<Loader2 className="rr-status-banner-spinner" />
							<div>
								<p className="rr-status-banner-title">Ollie is running gap analysis…</p>
								<p className="rr-status-banner-sub">
									Cross-referencing submitted evidence against {job.jurisdiction_record?.name ?? job.jurisdiction} -{" "}
									{getInspectionTypeLabel(job.inspection_type)} requirements.
								</p>
							</div>
						</div>
					)}

					{/* FAILED: red banner with error_step and error_message from the job row. */}
					{isFailed && (
						<div className="rr-status-banner rr-status-banner-failed rr-animate">
							<XCircle className="rr-status-banner-icon" />
							<div>
								<p className="rr-status-banner-title">
									Gap analysis failed
									{job.error_step ? ` at ${job.error_step}` : ""}
								</p>
								<p className="rr-status-banner-sub">
									{job.error_message ?? "Ollie was unable to complete the analysis."}
								</p>
							</div>
						</div>
					)}

					{/* ── Quick-stat cards row ────────────────────────────────────────────────
					     Card 1 (double width): Ollie avatar + Ollie Status and Ollie Score
					     Card 2: Est. Time On-Site (formatted minutes) ── */}
					<div className="rr-quick-stats rr-animate" style={{ animationDelay: "160ms" }}>
						<div className={`rr-stat-card rr-stat-card-ollie ${ollieConfig.cardClass}`}>
							<div className="rr-ollie-card-content">
								<div className="rr-ollie-card-avatar">
									<OllieAvatar mood={ollieMoodForReadiness(readinessStatus)} className="rr-ollie-card-image" />
								</div>
								<div className="rr-ollie-card-metrics">
									<div>
										<p className="rr-stat-card-label">Ollie Status</p>
										<p className={`rr-stat-card-value ${ollieConfig.textClass}`}>
											<span className="rr-ollie-card-icon-wrap">
												<OllieIcon className={`rr-ollie-card-icon ${ollieConfig.textClass}`} />
											</span>
											{ollieConfig.label}
										</p>
									</div>
									<div>
										<p className="rr-stat-card-label">Ollie Score</p>
										<p className="rr-stat-card-value rr-stat-value-dark">
											{isComplete ? ollieScore : "—"}
											{isComplete && <span className="rr-ollie-card-score-total">/ 100</span>}
										</p>
									</div>
								</div>
							</div>
						</div>

						<div className="rr-stat-card">
							<div className="rr-stat-card-content">
								<div>
									<p className="rr-stat-card-label">Est. Time On-Site</p>
									<p className="rr-stat-card-value rr-stat-value-dark">
										{formatEstimatedTime(
											computeEstimatedOnsiteMinutes(
												job?.estimated_onsite_minutes,
												job?.inspection_checklist_items ?? []
											)
										)}
									</p>
								</div>
								<div className="rr-stat-icon-wrap rr-stat-icon-wrap-gray">
									<Clock className="rr-stat-icon rr-stat-icon-gray" />
								</div>
							</div>
						</div>
					</div>

					{/* ── Two-column content grid ────────────────────────────────────────────
					     Left (rr-categories-col): per-category accordions
					     Right (rr-sidebar-col): on-site checklist + summary card ── */}
					<div className="rr-content-grid">
						<div className="rr-categories-col">
							<div className="rr-section-header rr-animate" style={{ animationDelay: "240ms" }}>
								<h2 className="rr-section-title">Category Analysis</h2>
								<span className="rr-section-count">{categories.length} Areas</span>
							</div>

							{categories.length === 0 && (
								<div className="rr-sidebar-card">
									<p className="rr-not-found-text">
										No checklist categories yet. Once Ollie finishes analysis, results will appear here.
									</p>
								</div>
							)}

							{/* Each category renders as a collapsible accordion card.
							     Inside: findings list (all Ollie observations) and
							     deficiencies list (only non-passing gaps). */}
							{categories.map((category, categoryIndex) => {
								const statusConfig = categoryStatusConfig[category.status];
								const StatusIcon = statusConfig.icon;
								// Every category is in the PDF, whatever the reader collapsed on screen.
								const isExpanded = isExporting || (expandedCategories[category.key] ?? true);

								return (
									<div
										key={category.key}
										className="rr-category-card rr-animate"
										style={{ animationDelay: `${280 + categoryIndex * 60}ms` }}
									>
										<button className="rr-category-header" onClick={() => toggleCategory(category.key)}>
											<div className="rr-category-header-left">
												<div className={`rr-category-icon-wrap ${statusConfig.badgeClass}`}>
													<Icon source={category.iconSource} name={category.iconName} size={24} />
												</div>
												<div>
													<h3 className="rr-category-name">{category.label}</h3>
													<p className="rr-category-sub">
														{category.itemsChecked}/{category.totalItems} {category.totalItems === 1 ? "item" : "items"}{" "}
														checked
													</p>
												</div>
											</div>
											<div className="rr-category-header-right">
												<span className={`rr-status-badge ${statusConfig.badgeClass}`}>
													<StatusIcon className="rr-status-badge-icon" />
													{statusConfig.label}
												</span>
												<ChevronDown className={`rr-chevron ${isExpanded ? "rr-chevron-expanded" : ""}`} />
											</div>
										</button>

										<div className={`rr-category-body ${isExpanded ? "rr-category-body-expanded" : ""}`}>
											<div className="rr-category-body-inner">
												{category.items.length > 0 ? (
													(() => {
														const { normalItems, deficiencyItems } = splitCategoryItems(category.items);

														return (
															<>
																{normalItems.length > 0 && (
																	<div className="rr-findings">
																		{normalItems.map(item => {
																			const assessment = getItemAssessment(item);
																			const gapVisual = gapVisualConfig[assessment.visual];
																			const GapVisualIcon = gapVisual.Icon;
																			return (
																				<div
																					key={item.id}
																					className="rr-finding rr-finding-clickable"
																					onClick={() => openItemEvidence(item.id)}
																				>
																					<GapVisualIcon className={gapVisual.iconClass} />
																					<span className={`rr-finding-text ${gapVisual.textClass}`}>
																						{item.item_name}
																					</span>
																					<div className="rr-view-badge">
																						<Eye className="rr-view-icon" />
																						<span>View</span>
																					</div>
																				</div>
																			);
																		})}
																	</div>
																)}
																{deficiencyItems.length > 0 && (
																	<div className="rr-deficiencies-section">
																		<h4 className="rr-deficiencies-title">DEFICIENCIES</h4>
																		<div className="rr-findings rr-findings-deficiencies">
																			{deficiencyItems.map(item => {
																				const assessment = getItemAssessment(item);
																				const gapVisual = gapVisualConfig[assessment.visual];
																				const GapVisualIcon = gapVisual.Icon;
																				return (
																					<div
																						key={item.id}
																						className="rr-finding rr-finding-clickable rr-finding-deficiency"
																						onClick={() => openItemEvidence(item.id)}
																					>
																						<div className={`rr-category-icon-wrap ${gapVisual.badgeClass}`}>
																							<GapVisualIcon className={gapVisual.iconClass} />
																						</div>
																						<div
																							className="rr-finding-content"
																							style={{ display: "flex", flexDirection: "column" }}
																						>
																							<span className={`rr-finding-text`}>{item.item_name}</span>
																							{assessment.reason && (
																								<span
																									className={`rr-finding-reason`}
																									style={{ fontSize: "0.85em", opacity: 0.8, marginTop: "2px" }}
																								>
																									{assessment.reason}
																								</span>
																							)}
																							{item.evidence_submissions.some(s => s?.location_verified === false) && (
																								<span
																									className="rr-finding-location-unverified"
																									style={{
																										fontSize: "0.8em",
																										fontStyle: "italic",
																										opacity: 0.75,
																										marginTop: "2px"
																									}}
																								>
																									Location verification unavailable for this item. Acknowledged and
																									captured on-site during pre-inspection.
																								</span>
																							)}
																							<div className="rr-view-badge">
																								<Eye className="rr-view-icon" />
																								<span>View</span>
																							</div>
																						</div>
																					</div>
																				);
																			})}
																		</div>
																	</div>
																)}
															</>
														);
													})()
												) : (
													<p className="rr-empty-category">No checklist items in this category.</p>
												)}
											</div>
										</div>
									</div>
								);
							})}
						</div>

						<div className="rr-sidebar-col">
							{/* ── On-site verification checklist sidebar card ────────────────────────
							     Lists corrective actions that require physical on-site verification,
							     sorted by priority_rank (Ollie assigns rank 1 = highest urgency).
							     Items with corrective_action = null/"null" are excluded. ── */}
							<div className="rr-sidebar-card rr-sidebar-card-ollie rr-animate" style={{ animationDelay: "240ms" }}>
								<div className="rr-sidebar-card-glow" />
								<div className="rr-checklist-header">
									<div className="rr-checklist-icon">
										<ClipboardList className="rr-checklist-icon-glyph" />
									</div>
									<div>
										<h3 className="rr-sidebar-card-title">On-Site Verification Priorities</h3>
									</div>
								</div>
								{onSiteChecklist.length > 0 ? (
									<div className="rr-checklist-items">
										{onSiteChecklist.map((onSiteItem, onSiteIndex) => (
											<div key={onSiteItem.id} className="rr-checklist-item">
												<div className="rr-checklist-num">
													<span>{onSiteIndex + 1}</span>
												</div>
												<p className="rr-checklist-text">{onSiteItem.text}</p>
											</div>
										))}
									</div>
								) : (
									<p className="rr-empty-category">
										{isAnalyzing
											? "Ollie will populate the on-site checklist once analysis completes."
											: "Recommendations will be listed here."}
									</p>
								)}
							</div>

							{/* ── Summary sidebar card ───────────────────────────────────────────────
							     Shows global counts (verified / warnings / blocking) and job metadata
							     (creator, timestamps, last state transition), plus admin-only diagnostics. ── */}
							<div className="rr-sidebar-card rr-animate" style={{ animationDelay: "300ms" }}>
								<h3 className="rr-sidebar-card-title rr-summary-title">Summary</h3>

								<div className="rr-summary-rows">
									<div className="rr-summary-row rr-summary-row-ready">
										<div className="rr-summary-row-label">
											<CheckCircle2 className="rr-summary-row-icon rr-icon-green" />
											<span>Verified</span>
										</div>
										<span className="rr-summary-count rr-count-green">{totals.verified}</span>
									</div>

									{totals.warnings > 0 && (
										<div className="rr-summary-row rr-summary-row-warning">
											<div className="rr-summary-row-label">
												<AlertTriangle className="rr-summary-row-icon rr-icon-amber" />
												<span>Warnings</span>
											</div>
											<span className="rr-summary-count rr-count-amber">{totals.warnings}</span>
										</div>
									)}

									{totals.blocking > 0 && (
										<div className="rr-summary-row rr-summary-row-fail">
											<div className="rr-summary-row-label">
												<XCircle className="rr-summary-row-icon rr-icon-red" />
												<span>Blocking</span>
											</div>
											<span className="rr-summary-count rr-count-red">{totals.blocking}</span>
										</div>
									)}

									{/* Missing Evidence: checklist items that are incomplete (no evidence submitted). */}
									{totals.missingEvidence > 0 && (
										<div className="rr-summary-row rr-summary-row-missing">
											<div className="rr-summary-row-label">
												<CircleDashed className="rr-summary-row-icon rr-icon-slate" />
												<span>Missing Evidence</span>
											</div>
											<span className="rr-summary-count rr-count-slate">{totals.missingEvidence}</span>
										</div>
									)}
								</div>

								<div className="rr-summary-meta">
									<div className="rr-summary-meta-row">
										<span className="rr-summary-meta-label">Total Items Checked</span>
										<span className="rr-summary-meta-value">{totals.totalItemsChecked}</span>
									</div>
									<div className="rr-summary-meta-row">
										<span className="rr-summary-meta-label">Total Categories</span>
										<span className="rr-summary-meta-value">{categories.length}</span>
									</div>
									<div className="rr-summary-meta-row">
										<span className="rr-summary-meta-label">Total Citations</span>
										<span className="rr-summary-meta-value">{totals.citations}</span>
									</div>
									{isPlatformAdmin && (
										<>
											<div className="rr-summary-meta-row">
												<span className="rr-summary-meta-label">Ollie Confidence</span>
												<span className="rr-confidence-badge">{ollieConfidence}</span>
											</div>
											<div className="rr-summary-meta-row">
												<span className="rr-summary-meta-label">Job Status</span>
												<span className="rr-summary-meta-value">{jobState ?? "—"}</span>
											</div>
											{job.organization?.type && (
												<div className="rr-summary-meta-row">
													<span className="rr-summary-meta-label">Org Type</span>
													<span className="rr-summary-meta-value">{job.organization.type}</span>
												</div>
											)}
										</>
									)}
									{job.user && (
										<div className="rr-summary-meta-row">
											<span className="rr-summary-meta-label">Created by</span>
											<span className="rr-summary-meta-value">
												{job.user.first_name} {job.user.last_name}
											</span>
										</div>
									)}
									{job.user?.email && (
										<div className="rr-summary-meta-row">
											<span className="rr-summary-meta-label">Email</span>
											<span className="rr-summary-meta-value">{job.user.email}</span>
										</div>
									)}
									{job.checklist_generated_at && (
										<div className="rr-summary-meta-row">
											<span className="rr-summary-meta-label">Checklist generated</span>
											<span className="rr-summary-meta-value">
												{new Date(job.checklist_generated_at).toLocaleDateString()}
											</span>
										</div>
									)}
									{job.analysis_started_at && (
										<div className="rr-summary-meta-row">
											<span className="rr-summary-meta-label">Analysis started</span>
											<span className="rr-summary-meta-value">
												{new Date(job.analysis_started_at).toLocaleString()}
											</span>
										</div>
									)}
									{job.analysis_completed_at && (
										<div className="rr-summary-meta-row">
											<span className="rr-summary-meta-label">Analysis completed</span>
											<span className="rr-summary-meta-value">
												{new Date(job.analysis_completed_at).toLocaleString()}
											</span>
										</div>
									)}
									{lastStateLog && (
										<div className="rr-summary-meta-row">
											<span className="rr-summary-meta-label">Last state change</span>
											<span className="rr-summary-meta-value">
												{lastStateLog.from_state ?? "∅"} → {lastStateLog.to_state}
											</span>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			{/* ── EvidenceModal ───────────────────────────────────────────────────────
			     Full-screen overlay that renders the submitted photo/video/document.
			     Shows Ollie analysis text and lets inspectors submit feedback. Rendered outside
			     the main scroll container so it overlays everything. Closed by clearing the
			     selected id. The modal reads the logged-in user itself, so no user prop. ── */}
			{selectedItem && (
				<EvidenceModal
					isOpen={!!selectedItem}
					onClose={closeItemEvidence}
					item={selectedItem}
					onPrevious={() => previousItemId && setSelectedItemId(previousItemId)}
					onNext={() => nextItemId && setSelectedItemId(nextItemId)}
					hasPrevious={!!previousItemId}
					hasNext={!!nextItemId}
				/>
			)}
		</OllieLayout>
	);
};

export default InspectionDetails;
